import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, Crosshair, FileText, PanelRightOpen, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { editorApiError, getEditorDocument, openEditorSession } from "../api";
import { FREEFORM_EDITOR_KINDS, type EditorDocKind, type EditorDocumentStatus, type EditorSession } from "../types";

/**
 * Edycja pisma ODT w przeglądarce — Collabora Online w <iframe> na CAŁYM ekranie
 * (nie w modalu: zapis i zamknięcie ma własne w swoim pasku, a duplikaty w ramce
 * tylko myliły i kusiły do zapisywania dwiema drogami). Host:
 *  1. otwiera sesję (POST /office-editor/sessions/) i wysyła token FORMULARZEM do ramki,
 *     tak jak zaleca Collabora (token nie ląduje w adresie ramki);
 *  2. rozmawia z edytorem przez postMessage (Host_PostmessageReady, UI_Save, UI_Close,
 *     Doc_ModifiedStatus) — zapis idzie protokołem WOPI do backendu, który tworzy NOWĄ
 *     WERSJĘ dokumentu (a przy formularzu WPW konwertuje ją do PDF);
 *  3. w wysuwanym panelu wylicza pola ręczne `#wpisz_…#` szablonu; kliknięcie zaznacza
 *     pole w dokumencie (.uno:ExecuteSearch), a po każdym zapisie odświeża listę tego,
 *     co jeszcze zostało — pakiet nie skompiluje się, dopóki lista nie jest pusta.
 *
 * ZAPIS TYLKO NA ŻĄDANIE: autozapis Collabory jest wyłączony w konfiguracji, a backend
 * odrzuca też zapis przy zamykaniu ramki (wopi.py). Dlatego zamknięcie z niezapisanymi
 * zmianami PYTA o potwierdzenie — domyślną akcję krzyżyka Collabory przejmujemy przez
 * Disable_Default_UIAction, a wyjście ze strony pilnuje beforeunload.
 */
export function OdtEditorScreen({
  kind, id, title, readOnly, onClose, onSaved,
}: {
  kind: EditorDocKind;
  id: string;
  title?: string;
  /** Otwórz TYLKO do podglądu, choćby backend dał prawo zapisu (podgląd „jak podwykonawca"). */
  readOnly?: boolean;
  /** Zamknięcie edytora (po zapisie albo porzuceniu). `saved` = był choć jeden zapis. */
  onClose: (saved: boolean) => void;
  /** Po każdym udanym zapisie — świeży stan slotu (nowa wersja, brakujące pola). */
  onSaved?: (status: EditorDocumentStatus) => void;
}) {
  const [session, setSession] = useState<EditorSession | null>(null);
  const [status, setStatus] = useState<EditorDocumentStatus | null>(null);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [modified, setModified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const savedOnce = useRef(false);
  const uiSavePending = useRef(false);
  // Stan „zmodyfikowany" do użytku w nasłuchu postMessage (domknięcie efektu jest stare).
  const modifiedRef = useRef(false);
  useEffect(() => {
    modifiedRef.current = modified;
  }, [modified]);

  // Bezpiecznik: zapis, na który edytor nie odpowiedział (nie przeszedł w „niezmodyfikowany"),
  // nie może trzymać komunikatu „Zapisuję…" w nieskończoność.
  useEffect(() => {
    if (!saving) return;
    const timer = window.setTimeout(() => {
      uiSavePending.current = false;
      setSaving(false);
      setSaveMsg({ kind: "error", text: "Edytor nie potwierdził zapisu w ciągu 90 s. Zapisz jeszcze raz (dyskietka w pasku edytora); w razie czego pobierz plik i zapisz go lokalnie." });
    }, 90_000);
    return () => window.clearTimeout(timer);
  }, [saving]);
  // Potwierdzenie zapisu znika samo; komunikat o problemie zostaje, aż ktoś go zamknie.
  useEffect(() => {
    if (saveMsg?.kind !== "ok") return;
    const timer = window.setTimeout(() => setSaveMsg(null), 8_000);
    return () => window.clearTimeout(timer);
  }, [saveMsg]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const frameName = useMemo(() => `odt-editor-${kind}-${id}`, [kind, id]);
  // Plik „dowolny" (dysk, zakładki „Pliki", załączniki): bez pól ręcznych i PDF-a do kontroli.
  const freeform = FREEFORM_EDITOR_KINDS.has(kind);

  // Origin edytora = origin adresu ramki; wiadomości spoza niego ignorujemy.
  const editorOrigin = useMemo(() => {
    if (!session) return "";
    try {
      return new URL(session.editor_url).origin;
    } catch {
      return "";
    }
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    // Bez `readOnly` wołamy jak dotąd (dwa argumenty) — tryb i tak rozstrzyga backend.
    (readOnly ? openEditorSession(kind, id, { readOnly: true }) : openEditorSession(kind, id))
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        setStatus(s.document);
      })
      .catch((e) => !cancelled && setError(editorApiError(e)));
    return () => {
      cancelled = true;
    };
  }, [kind, id, readOnly]);

  // Token idzie POST-em do ramki — po zamontowaniu formularza wysyłamy go raz.
  useEffect(() => {
    if (session && formRef.current) formRef.current.submit();
  }, [session]);

  // Edytor, który w rozsądnym czasie nie zgłosił Document_Loaded, nie zaskoczy już sam —
  // zamiast wiecznego „Ładuję dokument" pokazujemy powód i wyjście awaryjne.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!session || loaded || error) return;
    const timer = window.setTimeout(() => setStalled(true), 45_000);
    return () => window.clearTimeout(timer);
  }, [session, loaded, error]);

  const post = useCallback(
    (messageId: string, values: Record<string, unknown> = {}) => {
      const win = iframeRef.current?.contentWindow;
      if (!win || !editorOrigin) return;
      win.postMessage(JSON.stringify({ MessageId: messageId, SendTime: Date.now(), Values: values }), editorOrigin);
    },
    [editorOrigin],
  );

  const currentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (status) currentIdRef.current = status.current_id;
  }, [status]);

  /** Świeży stan slotu. Po zapisie Collabora melduje „niezmodyfikowany", zanim plik
   *  dotrze do backendu protokołem WOPI — więc `afterSave` odpytuje aż pojawi się NOWA
   *  wersja (inny current_id), najwyżej kilkanaście sekund. */
  const refreshStatus = useCallback(async (afterSave = false) => {
    const previous = currentIdRef.current;
    let fresh: EditorDocumentStatus | null = null;
    for (let attempt = 0; attempt < (afterSave ? 15 : 1); attempt++) {
      try {
        fresh = await getEditorDocument(kind, id);
      } catch {
        fresh = null;
      }
      if (!afterSave || (fresh && fresh.current_id !== previous)) break;
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    if (fresh) {
      setStatus(fresh);
      if (fresh.current_id !== previous) onSaved?.(fresh);
    }
    // `changed` = backend faktycznie ma nową wersję. Collabora potrafi zgłosić sukces
    // zapisu, zanim (albo mimo że) wysyłka do WOPI padła — bez tego host mówił
    // „Zapisano", a plik został stary.
    return { fresh, changed: !!fresh && fresh.current_id !== previous };
  }, [kind, id, onSaved]);

  // PDF do kontroli powstaje w tle: zapis WOPI oddaje połączenie od razu (inaczej
  // Collabora przerywa kolejny zapis błędem „upload already in progress"), więc link
  // pojawia się chwilę po samej wersji. Dopytujemy, zamiast kazać wchodzić ponownie —
  // z limitem, bo po dwóch minutach to już nie jest „zaraz będzie" i komunikat o
  // niepowodzeniu i tak przyjdzie w `conversion_error`.
  const pdfPollsRef = useRef(0);
  useEffect(() => {
    if (!status?.pdf_pending) {
      pdfPollsRef.current = 0;
      return;
    }
    if (pdfPollsRef.current >= 48) return;
    const timer = window.setTimeout(() => {
      pdfPollsRef.current += 1;
      void refreshStatus();
    }, 2_500);
    return () => window.clearTimeout(timer);
  }, [status, refreshStatus]);

  const savedText = (fresh: EditorDocumentStatus | null) => {
    const left = fresh?.unfilled.length ?? 0;
    if (freeform) return { kind: "ok" as const, text: kind === "drive_file" ? "Zapisano plik na dysku." : "Zapisano plik." };
    return left
      ? { kind: "error" as const, text: `Zapisano nową wersję, ale ${left === 1 ? "zostało 1 pole" : `zostały ${left} pola`} do uzupełnienia.` }
      : { kind: "ok" as const, text: "Zapisano nową wersję — wszystkie pola uzupełnione." };
  };
  const notSavedText = {
    kind: "error" as const,
    text: "Edytor zgłosił zapis, ale serwer nie przyjął nowej wersji. Zapisz ponownie; jeśli to się powtórzy, pobierz plik i zapisz go lokalnie.",
  };

  /** Zamknięcie edytora. Niezapisane zmiany PRZEPADAJĄ (zapis przy zamykaniu jest
   *  odrzucany po stronie backendu), więc najpierw pytamy. */
  const requestClose = useCallback(() => {
    if (modifiedRef.current
        && !window.confirm("Dokument ma niezapisane zmiany — zamknięcie je porzuci. Zamknąć bez zapisu?")) return;
    onClose(savedOnce.current);
  }, [onClose]);

  useEffect(() => {
    if (!editorOrigin) return;
    const handler = (event: MessageEvent) => {
      if (event.origin !== editorOrigin) return;
      let msg: { MessageId?: string; Values?: Record<string, unknown> };
      try {
        msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      const values = (msg.Values ?? {}) as Record<string, unknown>;
      switch (msg.MessageId) {
        case "App_LoadingStatus":
          if (values.Status === "Document_Loaded") {
            post("Host_PostmessageReady");
            // Krzyżyk i „Plik ▸ Zamknij dokument" mają tylko ZGŁOSIĆ zamiar zamknięcia:
            // bez tego Collabora od razu rozbiera edytor, a my nie zdążylibyśmy
            // ostrzec o niezapisanych zmianach.
            post("Disable_Default_UIAction", { action: "UI_Close", disable: true });
            setLoaded(true);
          } else if (values.Status === "Failed") {
            setError("Edytor nie otworzył dokumentu. Pobierz plik i popraw lokalnie.");
          }
          break;
        case "UI_Save":
          // Zapis z paska Collabory (dyskietka / Ctrl+S / Plik ▸ Zapisz): edytor sam
          // wysyła plik do WOPI — backend robi z niego nową wersję — ale nie odsyła
          // potwierdzenia hostowi. Kolejne „niezmodyfikowany" oznacza, że zapis dotarł.
          // Dokument BEZ zmian Collabora zapisuje po cichu — wtedy nie ma na co czekać.
          if (!modifiedRef.current) break;
          uiSavePending.current = true;
          setSaving(true);
          setSaveMsg(null);
          break;
        case "Doc_ModifiedStatus": {
          const isModified = values.Modified === true || values.Modified === "true";
          setModified(isModified);
          if (!isModified && uiSavePending.current) {
            uiSavePending.current = false;
            savedOnce.current = true;
            setSaving(false);
            void refreshStatus(true).then(({ fresh, changed }) => {
              setSaveMsg(changed ? savedText(fresh) : notSavedText);
            });
          }
          break;
        }
        case "UI_Close":
        case "App_Close":
          requestClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [editorOrigin, post, refreshStatus, requestClose]);

  // Odświeżenie / wyjście ze strony z niezapisanymi zmianami — przeglądarka pyta sama.
  useEffect(() => {
    if (!modified) return;
    const guard = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [modified]);

  // Esc działa tylko wtedy, gdy fokus jest poza ramką (klawisze z wnętrza Collabory nie
  // docierają do rodzica) — stąd wyjście „na serio" siedzi w pasku edytora.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [requestClose]);

  /** Zaznacz pole w dokumencie — ten sam UNO, którego używa pasek „Znajdź" Collabory. */
  const locate = (name: string) => {
    post("Send_UNO_Command", {
      Command: ".uno:ExecuteSearch",
      Args: {
        "SearchItem.SearchString": { type: "string", value: `#${name}#` },
        "SearchItem.Backward": { type: "boolean", value: false },
        "SearchItem.Command": { type: "long", value: 0 },
      },
    });
  };

  const manual = status?.manual_fields ?? [];
  const unfilled = new Set(status?.unfilled ?? []);
  const otherLeftovers = (status?.unfilled ?? []).filter((n) => !manual.includes(n));
  // Tryb odczytu: udostępnienie „view" na dysku albo format, który Collabora tylko
  // pokazuje (PDF, obrazy).
  const viewOnly = readOnly || status?.can_write === false;
  const heading = title ?? (status ? `${viewOnly ? "Podgląd" : "Edycja"}: ${status.label}` : "Edycja pisma");
  const hasPanel = manual.length > 0 || otherLeftovers.length > 0 || (!freeform && !viewOnly);
  const leftCount = status?.unfilled.length ?? 0;

  // Edytor zajmuje CAŁY ekran, więc musi leżeć nad każdą warstwą strony, z której go
  // otwarto: nad szufladą karty działki (z-[1200], portal na body), nad panelami mapy
  // Leaflet (do z 1000, poza stacking contextem mapy) i nad zwykłymi modalami (z-50) —
  // przy z-50 mapa i karta zasłaniały część ramki. Wyżej zostają tylko dymki (9999).
  return createPortal(
    <div
      className="fixed inset-0 z-[1300] bg-[var(--color-bg-base)]"
      data-modal-layer
      data-testid="odt-editor-screen"
    >
      {error ? (
        <div className="h-full flex items-center justify-center p-6">
          <div className="max-w-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 space-y-3">
            <p className="text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
            </p>
            <Button size="sm" variant="secondary" onClick={() => onClose(savedOnce.current)}>
              <X size={13} className="mr-1" /> Zamknij
            </Button>
          </div>
        </div>
      ) : !session ? (
        <div className="h-full flex items-center justify-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Spinner size="sm" /> Otwieram edytor…
        </div>
      ) : (
        <>
          <form
            ref={formRef}
            method="post"
            action={session.editor_url}
            target={frameName}
            className="hidden"
            data-testid="odt-editor-form"
          >
            <input type="hidden" name="access_token" value={session.access_token} />
            <input type="hidden" name="access_token_ttl" value={String(session.access_token_ttl)} />
          </form>
          <iframe
            ref={iframeRef}
            name={frameName}
            title={heading}
            className="absolute inset-0 w-full h-full border-0 bg-white"
            allow="clipboard-read; clipboard-write"
            data-testid="odt-editor-frame"
          />

          {/* Zanim edytor się zamelduje, ramka jest biała — nad nią stan ładowania
              i wyjście awaryjne, gdyby dokument nie wstał wcale. */}
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-bg-base)]/90">
              {stalled ? (
                <div className="max-w-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4 space-y-3">
                  <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                      Edytor nie zgłosił załadowania dokumentu. Jeśli ramka pokazuje błąd,
                      serwer edytora nie może pobrać pliku (adres WOPI, nazwa pliku, token).
                      Zamknij, spróbuj ponownie albo pobierz plik i popraw lokalnie.
                    </span>
                  </p>
                  <Button size="sm" variant="secondary" onClick={() => onClose(savedOnce.current)}>
                    <X size={13} className="mr-1" /> Zamknij
                  </Button>
                </div>
              ) : (
                <span className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
                  <Spinner size="sm" /> Ładuję dokument w edytorze…
                </span>
              )}
            </div>
          )}

          {/* Panel pól ręcznych i PDF-u — wysuwany z prawej krawędzi, żeby edytor miał
              cały ekran. Zwinięty pokazuje, ile pól jeszcze zostało. */}
          {hasPanel && loaded && (
            <div className="absolute top-1/3 right-0 flex items-start">
              {!panelOpen ? (
                <button
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  data-testid="odt-editor-panel-toggle"
                  className={`flex items-center gap-1 px-2 py-2 text-[11px] font-mono border border-r-0 shadow-sm ${
                    leftCount
                      ? "border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                      : "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]"
                  }`}
                  title="Pola do uzupełnienia i PDF bieżącej wersji"
                >
                  <PanelRightOpen size={13} />
                  {leftCount ? `${leftCount} do uzupełnienia` : "Pola"}
                </button>
              ) : (
                <aside className="w-72 max-h-[60vh] overflow-y-auto border border-r-0 border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg p-2 space-y-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="section-label truncate" title={heading}>{heading}</span>
                    <button
                      type="button"
                      onClick={() => setPanelOpen(false)}
                      className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                      title="Zwiń panel"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {(manual.length > 0 || otherLeftovers.length > 0) && (
                    <div className="space-y-1.5">
                      <div className="section-label">Pola do ręcznego uzupełnienia</div>
                      <ul className="space-y-1">
                        {manual.map((name) => {
                          const left = unfilled.has(name);
                          return (
                            <li key={name} className="flex items-center gap-1.5">
                              {left
                                ? <AlertTriangle size={12} className="shrink-0 text-amber-600" />
                                : <CheckCircle2 size={12} className="shrink-0 text-green-600" />}
                              <button
                                type="button"
                                className="font-mono text-[11px] text-left hover:underline disabled:no-underline disabled:opacity-60 truncate"
                                title={left ? "Kliknij, aby zaznaczyć pole w dokumencie" : "Uzupełnione"}
                                disabled={!left}
                                onClick={() => locate(name)}
                              >
                                #{name}#
                              </button>
                              {left && <Crosshair size={11} className="shrink-0 text-[var(--color-text-muted)]" />}
                            </li>
                          );
                        })}
                        {otherLeftovers.map((name) => (
                          <li key={name} className="flex items-center gap-1.5">
                            <AlertTriangle size={12} className="shrink-0 text-amber-600" />
                            <button
                              type="button"
                              className="font-mono text-[11px] text-left hover:underline truncate"
                              onClick={() => locate(name)}
                              title="Zmienna bez wartości zostawiona w dokumencie — wpisz ręcznie"
                            >
                              #{name}#
                            </button>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-[var(--color-text-muted)]">
                        Lista odświeża się po zapisie. Pakiet nie skompiluje się, dopóki w dokumencie
                        zostaje jakikolwiek znacznik <span className="font-mono">#…#</span>.
                      </p>
                    </div>
                  )}

                  {/* PDF bieżącej wersji — po zapisie od razu do kontroli, bez pobierania ODT. */}
                  {!freeform && (
                    <div className="space-y-1">
                      <div className="section-label">PDF do kontroli</div>
                      {/* Przy `pdf_pending` NIE pokazujemy linku, nawet jeśli jakiś PDF
                          jest: dla TOM II to poprzedni scalony tom, a podsunięty jako
                          „do kontroli" wyglądałby na świeży i bez wprowadzonej poprawki. */}
                      {status?.pdf?.url && !status?.pdf_pending ? (
                        <a
                          href={status.pdf.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-[11px] text-[var(--color-primary)] hover:underline break-all"
                          title="PDF ostatnio zapisanej wersji — otworzy się w nowej karcie"
                        >
                          <FileText size={12} className="shrink-0" /> {status.pdf.original_filename}
                          {typeof status.version === "number" ? ` (v${status.version})` : ""}
                        </a>
                      ) : (
                        <p className="text-[var(--color-text-muted)]">
                          {saving || status?.pdf_pending
                            ? "Konwertuję zapisaną wersję do PDF…"
                            : "Powstanie przy pierwszym zapisie."}
                        </p>
                      )}
                      {status?.conversion_error && (
                        <p className="text-red-700 dark:text-red-400">⚠ {status.conversion_error}</p>
                      )}
                    </div>
                  )}

                  {!viewOnly && (
                    <p className="text-[10px] text-[var(--color-text-muted)]">
                      Zapisuj dyskietką w pasku edytora albo <span className="font-mono">Ctrl+S</span> —
                      każdy zapis to nowa wersja, nic nie jest nadpisywane. Zamknięcie bez zapisu
                      porzuca zmiany (nie ma autozapisu).
                    </p>
                  )}
                </aside>
              )}
            </div>
          )}

          {/* Stan zapisu — nad paskiem stanu Collabory, znika po chwili. */}
          {(saving || saveMsg) && (
            <div
              className="absolute bottom-8 right-3 max-w-sm border border-[var(--color-border)] bg-[var(--color-bg-elevated)] shadow-lg px-3 py-2 text-xs flex items-start gap-2"
              data-testid="odt-editor-save-status"
            >
              {saving ? (
                <>
                  <Spinner size="sm" />
                  <span className="text-[var(--color-text-muted)]">Zapisuję nową wersję…</span>
                </>
              ) : (
                <>
                  <span className={saveMsg!.kind === "ok"
                    ? "text-green-700 dark:text-green-400"
                    : "text-amber-700 dark:text-amber-400"}>
                    {saveMsg!.text}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSaveMsg(null)}
                    className="ml-auto p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                    title="Ukryj"
                  >
                    <X size={12} />
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>,
    document.body,
  );
}
